<template lang="pug">
    section.team-tab
        .alert.info(v-if="!form.id") {{ $t('You cannot manage this team until the account is saved.') }}
        paginated-items(
            v-else
            :scope="scope"
            :type="key"
            :loading="loading"
            :items="teamMembersPaginated.items"
            :filters="filters"
            :show-filters="showFilters"
            :variables="teamMembersPaginated.variables"
            :all-columns="teamMembersPaginated.allColumns"
            :active-columns="activeColumns"
            :page-info="teamMembersPaginated.pageInfo"
            @pagination-size-change="changePaginationSize"
            @pagination-current-change="changeCurrentPage"
            @show-table-columns="changeTableColumns"
            @change-filter="changeFilter"
            @click="handleClick"
        )
            template(slot="top")
                el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                    .row
                        .left
                            el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="openTeamMemberInvitationDrawer")
                                | {{ $t('New team member') }}
                        .middle.hide-on-empty
                            span.info
                                | {{ $t('Found {total} team members', { total: teamMembersPaginated.pageInfo.total }) }}
                            template(v-if="bulk.action || teamMembersPaginated.pageInfo.selection.total > 0")
                                hr.vertical
                                span.info {{ $t('{total} selected', { total: teamMembersPaginated.pageInfo.selection.total }) }}
                        .right.hide-on-empty
                            .bulk-actions
                                el-select(
                                    v-model="bulk.action"
                                    size="mini"
                                    :placeholder="$t('Select bulk action...')"
                                    clearable
                                    disabled
                                )
                                    el-option(
                                        v-for="item in bulk.options"
                                        :key="item.value"
                                        :label="item.label"
                                        :value="item.value"
                                    )
                                transition(name="slide-decent-left")
                                    el-button(
                                        v-if="teamMembersPaginated.pageInfo.selection.total > 0 || bulk.action"
                                        type="primary"
                                        size="mini"
                                        :disabled="!bulk.action || teamMembersPaginated.pageInfo.selection.total <= 0"
                                        icon="el-icon-fa-check fas"
                                        @click.stop.prevent="event => handleBulkAction()"
                                    )
                                        | {{ $t('Go') }}
                            hr.vertical
                            el-button(
                                plain
                                size="mini"
                                type="primary"
                                icon="el-icon-fa-filter fas"
                                @click="toggleFilterVisiblity"
                                :disabled="disable.showFilters || hasFilters"
                            )
                                span(v-if="showFilters") {{ $t('Hide filters') }}
                                span(v-else) {{ $t('Show filters') }}

            template(v-slot:actions:th)
                .col-left(v-if="!showFilters")
                    //- https://codepen.io/danielhoppener/pen/xxKVbey
                    check-all(
                        :total="teamMembersPaginated.pageInfo.total"
                        :show-dropdown="teamMembersPaginated.pageInfo.lastPage > 1"
                        :checked="bulkSelection"
                        :is-indeterminate="slectionIndeterminate"
                        @check-all="checkAll"
                        @check-visible="checkVisible"
                    )
            template(v-slot:actions:filter)
                .col-left(v-if="showFilters" style="padding-left: 10px")
                    //- https://codepen.io/danielhoppener/pen/xxKVbey
                    check-all(
                        :total="teamMembersPaginated.pageInfo.total"
                        :show-dropdown="teamMembersPaginated.pageInfo.lastPage > 1"
                        :checked="bulkSelection"
                        :default-event="checkAllEvent"
                        :is-indeterminate="slectionIndeterminate"
                        @check-all="checkAll"
                        @check-visible="checkVisible"
                    )
            template(v-slot:actions="{ item }")
                .col-left
                    el-checkbox(
                        :key="item.id"
                        :value="isChecked(item.id)"
                        @change="checkItem(item)"
                    )

            template(v-slot:status="{ value }")
                .col-left {{ $t(value) }}

            //- A trailing column of its own: the row opens the rights drawer
            //- and the leading `actions` cell is the bulk checkbox, so booked
            //- time gets its own control rather than a competing click.
            //- Time belongs to ONE shop's desk, so a person administering
            //- several has to say which — picking one silently would put a
            //- number under the wrong shop's name.

        el-drawer(
            :title="teamMemberDrawerTitle"
            :visible.sync="drawer.visibility"
            :before-close="confirmDiscard(() => changedSince(drawer.formSnapshot, teamMember.form))"
            :wrapper-closable="false"
            :modal="true"
            size="680px"
            @opened="drawer.formSnapshot = snapshotOf(teamMember.form)"
            @closed="resetMemberDrawer"
        )
            el-form.team-member-modal-form(
                ref="drawer"
                :model="teamMember.form"
                label-position="top"
                :rules="teamMember.rules"
                v-loading="ui.form.loading"

                @submit.prevent.native="saveTeamMember"
            )
                .hidden
                    el-form-item(prop="email")
                    el-form-item(prop="gender")
                    el-form-item(prop="first_name")
                    el-form-item(prop="last_name")

                TeamMemberForm(
                    v-if="drawer.teamMember"
                    :team-member="drawer.teamMember"
                    :initiator="form"
                    :active-tab="drawer.tab"
                    :cms-options="options.cms"
                    @change-tab="tab => drawer.tab = tab"
                    @error="error => drawer.error = error"
                )


            .drawer-footer
                .left
                    .switch(v-if="showNotificationSwitch")
                        //- :inactive-text="$t('Verify immediately')"
                        el-switch(
                            v-if="isSuperUser && drawer.type !== 'existing'"
                            :active-text="$t('Send verification email')"
                            v-model="notifyTeamMember"
                        )
                        el-switch(
                            v-else-if="drawer.type === 'existing' && drawer.teamMember.teamUserPivot && ! drawer.teamMember.teamUserPivot.invitation_verified_at"
                            :active-text="$t('Resend invitation')"
                            v-model="notifyTeamMember"
                        )
                    .button(v-else)
                        el-button(
                            round
                            size="mini"
                            icon="el-icon-fa-times fas"
                            @click="closeTeamMemberDrawer"
                        ) {{ $t('Dismiss') }}
                .right
                    el-button(
                        v-if="showNotificationSwitch === true"
                        round
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="closeTeamMemberDrawer"
                    ) {{ $t('Dismiss') }}
                    el-button(
                        round
                        size="mini"
                        :disabled="ui.form.loading || ui.form.saving || drawer.error"
                        :loading="ui.form.saving"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        @click="saveTeamMember"
                    ) {{ $t('Save and close') }}

        //- el-dialog(
        //-     :title="modalTitle"
        //-     :visible.sync="modalVisibility"
        //-     :close-on-click-modal="false"
        //-     top="10vh"
        //-     :width="modalType === 'existing' && modal.teamUserPivot && ! modal.teamUserPivot.invitation_verified_at ? '580px' : '460px'"
        //-     @closed="resetModal"
        //- )
        //-     el-form.user-modal-form(
        //-         :model="modal"
        //-         label-position="top"
        //-         :rules="rules"
        //-         ref="modal"
        //-         v-loading="modalLoading"
        //-         @submit.prevent.native="submitModalForm"
        //-     )
        //-         el-form-item(v-if="modalType !== 'existing'" :label="$t('Email')" prop="email")
        //-             el-input(v-model="modal.email")
        //-         el-form-item(v-if="isSuperUser && modalType !== 'existing'")
        //-             .switch
        //-                 //- :inactive-text="$t('Verify immediately')"
        //-                 el-switch(
        //-                     :active-text="$t('Send verification email')"
        //-                     v-model="modal.notify"
        //-                 )
        //-         el-form-item(v-if="ownedCmss.length > 0" :label="$t('CMS administration rights')")
        //-             .switch(v-for="cms in ownedCmss")
        //-                 el-switch(
        //-                     :active-text="cms.name"
        //-                     :value="isAllowedToAdministrate(cms)"
        //-                     @change="value => toggleAdministrationRight(cms, value)"
        //-                 )
        //-         el-alert(v-else type="info" :title="$t('There are no CMSs to administrate.')" :closable="false")
        //-     template(slot="footer")
        //-         .modal-footer
        //-             .left
        //-                 .switch
        //-                     el-switch(
        //-                         v-if="modalType === 'existing' && modal.teamUserPivot && ! modal.teamUserPivot.invitation_verified_at"
        //-                         :active-text="$t('Resend verification email')"
        //-                         v-model="notifyTeamMember"
        //-                     )
        //-             .right
        //-                 el-button(
        //-                     size="mini"
        //-                     icon="el-icon-fa-times fas"
        //-                     @click="closeModal"
        //-                 ) {{ $t('Dismiss') }}
        //-                 el-button(
        //-                     :disabled="modalLoading"
        //-                     size="mini"
        //-                     type="primary"
        //-                     icon="el-icon-fa-check fas"
        //-                     @click="submitModalForm"
        //-                 ) {{ $t('Ok') }}

        el-dialog.bulk-action-dialog(
            :title="bulk.modal.title"
            :visible.sync="bulk.modal.visibility"
            :close-on-click-modal="false"
            :width="bulk.modal.width"
            top="10vh"
            @closed="resetBulkModal"
        )
            .bulk-modal-body(v-if="bulk.modal.body" v-html="bulk.modal.body")
            template(slot="footer")
                .modal-footer
                    .left
                        el-button(
                            v-for="button in bulk.modal.buttons.left" :key="button.label"
                            v-if="button.action"
                            :size="button.size || 'mini'"
                            :type="button.type"
                            :icon="button.icon"
                            @click="button.action"
                        ) {{ button.label }}
                    .right
                        el-button(
                            v-for="button in bulk.modal.buttons.right" :key="button.label"
                            v-if="button.action"
                            :size="button.size || 'mini'"
                            :type="button.type"
                            :icon="button.icon"
                            @click="button.action"
                        ) {{ button.label }}
</template>

<i18n>
{
    "de": {
        "Booked time": "Gebuchte Zeit",
        "Go": "Ausführen",
        "Select bulk action...": "Massenaktion auswählen...",
        "Found {total} team members": "{total} Teammitglieder gefunden",
        "{total} selected": "{total} ausgewählt",
        "You cannot manage this team until the account is saved.": "Sie können dieses Team nicht verwalten, solange der Account nicht gespeichert ist.",
        "Invite a new team member": "Ein neues Teammitglied einladen",
        "New team member": "Neues Teammitglied",
        "CMS administration rights": "Webauftritt-Verwaltungsrechte",
        "There are no CMSs to administrate.": "Es sind keine Webauftritte zum Verwalten ferfügbar.",
        "Send verification email": "Verifizieren per Email",
        "Resend verification email": "Email zur Verifizierung erneut senden",
        "Resend invitation": "Einladung erneut senden",
        "Verify immediately": "Umgehend verifizieren",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "An error occurred!": "Es ist ein Fehler aufgetreten!",
        "Team member list could not be fetched.": "Teammitglied-Liste konnte nicht abgerufen werden.",
        "Team member could not be saved.": "Teammitglied konnte nicht gespeichert werden.",
        "Team member saved successfully!": "Teammitglied erfolgreich gespeichert!",
        "Selection": "Auswahl",
        "Check all": "Alle auswählen",
        "Check visible": "Sichtbare auswählen",
        "Bulk action could not be processed.": "Massenaktion konnte nicht verarbeitet werden.",
        "Change status...": "Status ändern...",
        "Select desired status": "Gewünschten status wählen",
        "Delete...": "Löschen...",
        "Do you really want to delete {total} team members?": "Sollen {total} Teammitglieder wirklich gelöscht werden?",
        "Do you really want to delete the selected team member?": "Soll das ausgewählte Teammitglied wirklich gelöscht werden?",
        "Delete {total} team members": "{total} Teammitglieder löschen",
        "Bulk action processed successfully!": "Massenaktion erfolgreich verarbeitet!"
    },
    "en": {
        "Found {total} team members": "Found {total} team members",
        "{total} selected": "{total} selected",
        "Do you really want to delete {total} team members?": "Do you really want to delete {total} team members?",
        "Delete {total} team members": "Delete {total} team members"
    }
}
</i18n>

<script>

import _ from 'lodash'
import Vue from 'vue'
import uniqid from 'uniqid'

import pinia from '@/pinia'
import { useAuthStore, useAccountStore, useTeamMemberStore, useSetupStore } from '@/stores'

import PaginatedItems from '@/components/PaginatedItems'
import CheckAll from '@/components/CheckAll'

import TeamMemberForm from '@/views/system/Account/Form/Partials/TeamMemberForm';
import discardGuard from '@/mixins/discardGuard';

export default {
    name: 'Team',
    mixins: [discardGuard],
    data() {
        return {
            scope: 'base',
            key: 'administrator_team',
            disable: {
                showFilters: false,
            },
            // modalVisibility: false,
            // modalLoading: false,
            // modalType: 'new', // valid options: 'new' / fresh / 'existing'
            // modal: {
            //     id: undefined,
            //     email: '',
            //     first_name: '',
            //     last_name: '',
            //     password: '',
            //     administrators: [],
            //     notify: true,
            // },
            drawer: {
                type: 'new',
                visibility: false,
                error: false,
                teamMember: undefined,
                tab: 'main',
                formSnapshot: undefined,
            },
            // TODO: this.$t() does not work propperly
            // i18n change will not reload this
            // find a solution (computed prop?)
            bulk: {
                action: undefined,
                options: [
                    { value: 'change-status', label: this.$t('Change status...')},
                    { value: 'delete', label: this.$t('Delete...') },
                ],
                modal: {
                    visibility: false,
                    width: '740px',
                    title: undefined,

                    body: undefined,
                    buttons: {
                        left: [
                            {
                                size: 'mini',
                                icon: 'el-icon-fa-times fas',
                                label: this.$t('Dismiss'),
                                action: this.closeBulkModal,
                            }
                        ],
                        right: [],
                    }
                }
            },
        }
    },
    components: {
        TeamMemberForm,
        PaginatedItems,
        CheckAll,
    },
    // watch: {
    //     users(users) {
    //         this.pagination.pageInfo.total = users.length
    //         this.pagination.pageInfo.lastPage = Math.ceil(users.length / this.pagination.pageInfo.perPage)
    //     }
    // },
    computed: {
        bulkSelection() {
            let checked = 'none';
            const total = _.get(this.teamMembersPaginated, 'pageInfo.total') || 0;

            const selection = this.teamMembersPaginated.pageInfo.selection;

            if (selection.type === 'exclusive' && selection.identifiers.length < total) checked = 'all';
            else if (selection.type === 'inclusive' && selection.identifiers.length) {
                checked = 'visible';
                // const items = _.get(this.teamMembersPaginated, 'items') || [];
                // const currentPageSelection = items.filter(el => selection.identifiers.includes(parseInt(el.id)));
                // if (currentPageSelection.length > 0) {
                //     checked = 'visible';
                // }
            }
            return checked;
        },
        slectionIndeterminate() {
            let indeterminate = false;

            const selection = this.teamMembersPaginated.pageInfo.selection;

            if (selection.type === 'exclusive' && selection.identifiers.length) indeterminate = true;
            else if (selection.type === 'inclusive' && selection.identifiers.length) {
                const items = _.get(this.teamMembersPaginated, 'items') || [];
                const currentPageSelection = items.filter(el => selection.identifiers.includes(parseInt(el.id)));
                indeterminate = currentPageSelection.length < items.length;
            }
            return indeterminate;
        },
        checkAllEvent() {
            let event = 'check-all';
            if (this.hasFilters) event = 'check-visible';
            return event;
        },

        activeColumns() {
            // return this.teamMembersPaginated.activeColumns.filter(el => {
            //     if (el.key === 'qty_sum' && this.manageStock === true) return el
            //     else if (el.key !== 'qty_sum') return el
            // })
            return this.teamMembersPaginated.activeColumns;
        },
        // showFilters() {
        //     return _.get(this.user.settings, `${this.scope}.pagination.${this.key}.showFilters`) || false
        // },
        showFilters() {
            return this.hasFilters || _.get(this.teamMembersPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            let filters = _.cloneDeep(this.teamMembersPaginated.filters);
            delete filters.initiator;
            return filters;
        },
        loading() {
            return this.ui.teamMembersPaginated.loading;
        },
        ownedCmss() {
            let cmss = []
            const administrators = _.get(this.form, 'administrators') || []

            administrators.forEach(admin => {
                if (admin.owner === true) cmss.push(admin.cms)
            })

            return cmss
        },
        // modalTitle() {
        //     let title = this.$t('New team member')
        //     if (this.modalType === 'existing') {
        //         if (this.modal.first_name || this.modal.last_name) {
        //             title = `${this.modal.first_name} ${this.modal.last_name}`
        //         } else {
        //             title = this.modal.email
        //         }
        //     }
        //     return title
        // },
        // rules() {
        //     let emails = this.user.email
        //     this.users.forEach(user => emails += `,${user.email}`)

        //     return {
        //         email: `required|email|excluded:${emails}`,
        //     }
        // },
        teamMemberDrawerTitle() {
            let title = this.$t('New team member')
            if (this.drawer.type === 'existing' && this.drawer.teamMember) {
                title = this.drawer.teamMember.name || this.drawer.teamMember.email
            }
            return title
        },
        notifyTeamMember: {
            get() {
                return _.get(this.teamMember.form, 'notify') || false
            },
            set(value) {
                this.teamMemberStore.setFormNotify(value)
            }
        },
        showNotificationSwitch() {
            return (this.isSuperUser && this.drawer.type !== 'existing')
                || (this.drawer.type === 'existing' && this.drawer.teamMember.teamUserPivot && ! this.drawer.teamMember.teamUserPivot.invitation_verified_at)
        },
        user() {
            return useAuthStore().user;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        accountStore() {
            return useAccountStore()
        },
        form() {
            return this.accountStore.form
        },
        options() {
            return this.accountStore.options
        },
        teamMemberStore() {
            return useTeamMemberStore()
        },
        teamMember() {
            return this.teamMemberStore.$state
        },
        ui() {
            return this.teamMemberStore.ui
        },
        teamMembersPaginated() {
            return this.teamMemberStore.teamMembersPaginated
        },
        setupStore() {
            return useSetupStore(pinia)
        }
    },
    methods: {
        isChecked(id) {
            let checked = false;

            const selection = this.teamMembersPaginated.pageInfo.selection;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(parseInt(id));
                    break;

                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(parseInt(id));
                    break;
            }
            return checked;
        },
        /**
         * A row opens that person's page - rights AND booked time on one
         * screen. It used to open a drawer holding only the rights, with the
         * figures on a separate page reached by a second button; one row, one
         * destination is the whole point of having refactored it.
         */
        handleClick(item) {
            this.$router.push({
                name: 'account-team-member',
                params: { id: this.form.id, userId: item.id },
            });
        },
        changePaginationSize(itemsPerPage) {
            this.updateLoading(true);
            this.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        changeCurrentPage(currentPage) {
            this.updateLoading(true);
            this.defineCurrentPage(currentPage);
            this.refetch();
        },
        changeTableColumns(visibleColumns) {
            this.defineActiveColumns(visibleColumns);
            // apolloClient.cache.reset()
            apolloClient.clearStore();
        },
        changeFilter(key, value, debounce = true) {
            this.defineCurrentPage(1);
            this.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters;

            let value = false;
            if (!visible) value = true;

            this.disable.showFilters = true;
            this.defineFilterVisibility(value);
            // this.saveFilterVisibility({ scope: 'cp', key: this.key, value })
            this.saveFilterVisibility({ scope: this.scope, key: this.key, value }).finally(
                () => (this.disable.showFilters = false)
            );
        },
        handleBulkAction(action = undefined) {
            if (! action) action = this.bulk.action;

            const completeRequest = ({ actions, data }) => {
                let promises = [];
                if (actions && actions.length) {
                    actions.forEach(async (action, i) => {
                        switch (action) {
                            case 'refetch-list':
                                await apolloClient.clearStore();
                                promises.push(new Promise(async (resolve, reject) => {
                                    await this.refetch();
                                    resolve();
                                }))
                                break;

                            default:
                                return reject('requested action is not supported');
                        }
                    })
                }

                return Promise.all(promises);
            }

            switch (action) {
                case 'change-status':
                    // show dialog
                    this.bulk.modal.title = this.$t('Select desired status');
                    this.bulk.modal.width = '380px';

                    this.bulk.modal.buttons.right = [
                        {
                            size: 'mini',
                            type: 'warning',
                            icon: 'el-icon-fa-ban fas',
                            label: this.$t('Inactive'),
                            action: () => this.handleBulkAction('change-status:disabled'),
                        },
                        {
                            size: 'mini',
                            type: 'success',
                            icon: 'el-icon-fa-check fas',
                            label: this.$t('Active'),
                            action: () => this.handleBulkAction('change-status:enabled'),
                        }
                    ];

                    this.bulk.modal.visibility = true;
                    break;

                case 'delete':
                    const total = this.teamMembersPaginated.pageInfo.selection.total;

                    // show dialog
                    this.bulk.modal.title = this.$t('Do you really want to delete {total} team members?', { total });
                    this.bulk.modal.width = '360px';

                    let confirmText = this.$t('Delete {total} team members', { total });

                    if (total === 1) {
                        this.bulk.modal.title = this.$t('Do you really want to delete the selected team member?');
                        confirmText = this.$t('Delete');
                    }

                    this.bulk.modal.buttons.right = [
                        {
                            size: 'mini',
                            type: 'danger',
                            icon: 'el-icon-fa-trash-alt fas',
                            label: confirmText,
                            action: () => this.handleBulkAction('delete:soft'),
                        }
                    ];

                    this.bulk.modal.visibility = true;
                    break;

                case 'delete:soft':
                case 'change-status:enabled':
                case 'change-status:disabled':
                    this.bulk.modal.visibility = false;

                    this.updateLoading(true);
                    this.processPaginationBulkAction(action)
                        .then(data => {
                            completeRequest(data)
                                .then(() => {
                                    if (action === 'delete:soft') this.resetBulkSelection();

                                    this.$notify.success({
                                        title: this.$t('Bulk action processed successfully!'),
                                        iconClass: 'fas el-icon-fa-check',
                                        position: 'bottom-left',
                                        customClass: 'dark',
                                        duration: 8000
                                    });
                                })
                                .finally(() => {
                                    this.updateLoading(false)
                                })
                        })
                        .catch(err => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);
                            this.updateLoading(false);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Bulk action could not be processed.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                    break;

                default:
                    this.updateLoading(false);
                    break;
            }
        },
        closeBulkModal() {
            this.bulk.modal.visibility = false;
        },
        resetBulkModal() {
            this.bulk.modal = {
                visibility: false,
                width: '740px',

                title: undefined,
                body: undefined,
                buttons: {
                    left: [
                        {
                            size: 'mini',
                            icon: 'el-icon-fa-times fas',
                            label: this.$t('Dismiss'),
                            action: this.closeBulkModal,
                        }
                    ],
                    right: [],
                }
            };
        },
        refetch() {
            this.updateLoading(true);
            this.fetchTeamMembersPaginated({ initiator: this.form.id, scope: 'base', type: 'administrator_team' })
                // .then(() => this.updateLoading(false))
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                    this.updateLoading(false);

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Team member list could not be fetched.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.updateLoading(false);
                })
        },

        debouncedRefetch: _.debounce(function() {
            // function scope is needed here
            this.updateLoading(true);
            this.refetch();
        }, 300),

        openTeamMemberInvitationModal(item = null) {
            // this.modalLoading = true
            // this.modalVisibility = true

            // if (item && item instanceof Event === false) {
            //     this.modal = _.cloneDeep(item)
            //     if (item && _.startsWith(item.id, 'new:')) {
            //         this.modalType = 'fresh'
            //     } else {
            //         this.modalType = 'existing'
            //     }
            // } else {
            //     this.modal.id = uniqid('new:')
            //     this.modalType = 'new'
            // }

            // this.modalLoading = false
        },
        openTeamMemberInvitationDrawer(item = null) {
            this.drawer.teamMember = { id: uniqid('new:') }
            this.drawer.type = 'new'

            this.drawer.visibility = true;
            this.notifyTeamMember = true;

            setTimeout(() => {
                // this.$refs.drawer.resetFields()
                this.$refs.drawer.clearValidate()
            }, 0)
        },
        // closeModal() {
        //     this.modalVisibility = false
        //     this.modalLoading = false
        // },
        closeTeamMemberDrawer() {
            this.drawer.visibility = false;
        },
        // submitModalForm() {
        //     if (this.modalType === 'new') {
        //         // add user to team
        //         this.$refs.modal.validate(valid => {
        //             if (valid) {
        //                 this.addUserToTeam(this.drawer)
        //                 this.closeModal()
        //             }
        //         })
        //     } else {
        //         // replace administrators in team-user
        //         this.updateUserInTeam(this.drawer)
        //         this.closeModal()
        //     }
        // },
        resetMemberDrawer() {
            // this.drawer = {
            //     id: undefined,
            //     email: '',
            //     first_name: '',
            //     last_name: '',
            //     password: '',
            //     administrators: [],
            //     notify: true,
            // }
            // this.$refs.modal.resetFields()

            this.drawer = {
                type: 'new',
                visibility: false,
                error: false,
                teamMember: undefined,
                tab: 'main'
            };

            this.updateLoading(false);
            this.updateSaving(false);

            // this.resetTeamMemberUI();
            this.resetTeamMemberForm();
            this.$nextTick(this.resetTeamMemberRules);
        },
        // toggleUserStatus(user, disable) {
        //     if (disable) this.disableUser(user)
        //     else this.enableUser(user)
        // },
        // isAllowedToAdministrate(cms) {
        //     const administrators = _.get(this.modal, 'administrators') || []
        //     return !! administrators.find(admin => admin.cms.id === cms.id && ! admin.deleted)
        // },
        // toggleAdministrationRight(cms, allowed) {
        //     const administrators = _.get(this.modal, 'administrators') || []
        //     const i = _.findIndex(administrators, admin => admin.cms.id === cms.id)

        //     if (! allowed) {
        //         // if (i >= 0) Vue.delete(this.modal.administrators, i)
        //         if (i >= 0) Vue.set(this.modal.administrators[i], 'deleted', true)
        //     } else {
        //         if (i >= 0) Vue.set(this.modal.administrators[i], 'deleted', false)
        //         else {
        //             const i = administrators.length
        //             const id = uniqid('new:')
        //             Vue.set(this.modal.administrators, i, {
        //                 id,
        //                 owner: false,
        //                 cms: {
        //                     id: cms.id,
        //                     name: cms.name
        //                 }
        //             })
        //         }
        //     }
        // },
        isVisibleCms(user, cms) {
            return user.administrators.find(admin => admin.cms.id === cms.id && ! admin.deleted)
                && this.ownedCmss.find(el => el.id === cms.id)
        },
        saveTeamMember() {
            this.$refs.drawer.validate((valid, errors) => {
                // console.log('valid', valid, errors);

                if (valid) {
                    this.updateSaving(true);

                    let save = this.update
                    if (! this.teamMember.form.id || this.teamMember.form.id.startsWith('new:')) save = this.create

                    save({ teamMember: this.teamMember.form, initiator: this.form.id })
                        .then(() => {
                            this.refetch();
                            this.closeTeamMemberDrawer();
                            this.updateSaving(false);
                            this.$notify.success({
                                title: this.$t('Team member saved successfully!'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        })
                        .catch(err => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);
                            this.updateSaving(false);
                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Team member could not be saved.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    // check the invalid fields
                    // and go to the corresponding tab
                    // or open the corresponding closed area
                    // TODO: scroll to invalid field
                    // let invalidTabs = [];
                    // _.each(errors, (error, path) => {
                    //     if (_.indexOf(['nr', 'status', 'name', 'categories', 'media'], path) >= 0) {
                    //         invalidTabs.push('main');
                    //     }
                    //     if (_.startsWith(path, 'prices.')) {
                    //         invalidTabs.push('prices');
                    //     }
                    //     // TODO: stock
                    // });
                    // invalidTabs = _.uniq(invalidTabs);

                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            });
        },
        // ...mapMutations({
        //     enableUser: 'account/form/team/userEnable',
        //     disableUser: 'account/form/team/userDisable',
        //     deleteUserFromTeam: 'account/form/team/markUserAsDeleted',
        //     restoreUserToTeam: 'account/form/team/unmarkUserAsDeleted',
        //     addUserToTeam: 'account/form/team/addUser',
        //     updateUserInTeam: 'account/form/team/updateUser',
        // }),
        // ...mapActions({
        //     saveFilterVisibility: 'saveFilterVisibility',
        // }),

        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload)
        },
        fetchTeamMember(payload) {
            return this.teamMemberStore.fetchTeamMember(payload);
        },
        fetchTeamMembersPaginated(payload) {
            return this.teamMemberStore.fetchTeamMembersPaginated(payload);
        },
        processPaginationBulkAction(action) {
            return this.teamMemberStore.processPaginationBulkAction(action);
        },
        create(payload) {
            return this.teamMemberStore.create(payload);
        },
        update(payload) {
            return this.teamMemberStore.update(payload);
        },
        resetTeamMemberForm() {
            return this.teamMemberStore.reset();
        },
        resetTeamMemberUI() {
            this.teamMemberStore.resetUI();
        },
        resetTeamMemberRules() {
            this.teamMemberStore.resetRules();
        },
        updateLoading(value) {
            this.teamMemberStore.updatePaginationLoading(value);
        },
        updateSaving(value) {
            this.teamMemberStore.updateFormSaving(value);
        },
        defineItemsPerPage(value) {
            this.teamMemberStore.defineItemsPerPage(value);
        },
        defineCurrentPage(value) {
            this.teamMemberStore.defineCurrentPage(value);
        },
        defineActiveColumns(value) {
            this.teamMemberStore.defineActiveColumns(value);
        },
        defineFilterValue(payload) {
            this.teamMemberStore.defineFilterValue(payload);
        },
        defineFilterVisibility(value) {
            this.teamMemberStore.defineFilterVisibility(value);
        },
        checkAll(payload) {
            this.teamMemberStore.paginationBulkSelectionCheckAll(payload);
        },
        checkVisible(payload) {
            this.teamMemberStore.paginationBulkSelectionCheckVisible(payload);
        },
        checkItem(item) {
            this.teamMemberStore.paginationBulkSelectionCheckItem(item);
        },
        resetBulkSelection() {
            this.teamMemberStore.paginationBulkSelectionReset();
        }
    },
    mounted() {
        // this.pagination.pageInfo.total = this.users.length
        // this.pagination.pageInfo.lastPage = Math.ceil(this.users.length / this.pagination.pageInfo.perPage)
    }
}
</script>

<style lang="sass" scoped>
.account
    .alert
        margin-left: 1rem
        margin-right: 1rem
    section.team-tab
        padding: 0
        :deep()
            .column.name
                position: relative
                .will-be-notified
                    position: absolute
                    left: -15px
                    top:  0
                    font-size: 11px
                    color: $--color-info

.el-form > .el-form-item:last-child
    margin-bottom: 0
.modal-footer,
.drawer-footer
    display: flex
    justify-content: space-between
    .left
        display: flex
        align-items: center
        .switch
            height: 28px
            line-height: 28px

.table-top
    display: block
    .row
        display: flex
        justify-content: space-between
    .import
        margin-top: .5rem
    .middle, .right
        display: flex
        align-items: center
    hr.vertical
        display: inline-block
        margin: 0 0.7rem
        border: none
        border-right: 1px solid $--border-color-base
        height: calc(100% - 10px)

.paginated-items .table-top .right .bulk-actions
    .el-select
        margin-right: 0
    .el-button
        margin-left: 10px

.bulk-action-dialog:deep()
    .el-dialog__body
        padding: 0
        .bulk-modal-body
            padding: 20px
    .el-dialog__header
        padding-right: 50px

.el-loading-parent--relative
    .table-top.el-loading-parent--relative:deep()
        .el-loading-mask
            display: none
    .paginated-items:deep() tbody.loading
        opacity: initial

.team-member-modal-form
    overflow-y: auto
</style>
